import { z } from "zod";
import type { FormValues } from "~/components/form-engine";
import { VALIDATION_KEYS } from "~/components/form-engine/types";

// Constants
const FILE_MAX_SIZE_BYTES = 2 * 1024 * 1024; // 2MB

// Submission context with token info
export interface SubmissionContext {
  tokenId: string;         // Raw token
  onboardingId: string;    // Onboarding ID from validation
  enterpriseId: string;
  partialRfc: string;
  partialRazonSocial: string;
}

// Validation schema for SAT onboarding
// Uses i18n keys that get translated at display time
const satOnboardingSchema = z
  .object({
    // Welcome step
    startOnboarding: z.literal(true, {
      message: VALIDATION_KEYS.required,
    }),

    // Company verification
    // RFC format: 3-4 letters (including Ñ, &) + 6 digits (date) + 3 alphanumeric (homoclave)
    rfc: z
      .string()
      .min(12, VALIDATION_KEYS.minLength)
      .max(13, VALIDATION_KEYS.maxLength)
      .regex(/^[A-Za-zÑñ&]{3,4}\d{6}[A-Za-z0-9]{3}$/, "onboarding.validation.invalidRfc"),
    razonSocial: z
      .string()
      .min(3, VALIDATION_KEYS.minLength)
      .max(150, VALIDATION_KEYS.maxLength),
    regimenFiscal: z.enum([
      "601", "603", "605", "606", "607", "608", "610", "611", "612",
      "614", "615", "616", "620", "621", "622", "623", "624", "625", "626"
    ], { message: VALIDATION_KEYS.required }),

    // SAT Credentials
    cedulaFiscal: z
      .instanceof(File, { message: VALIDATION_KEYS.required })
      .refine((file) => file.type === "application/pdf", {
        message: VALIDATION_KEYS.invalidFile,
      })
      .refine((file) => file.size <= FILE_MAX_SIZE_BYTES, {
        message: VALIDATION_KEYS.fileTooLarge,
      }),
    satUsername: z
      .string()
      .min(3, VALIDATION_KEYS.minLength)
      .max(50, VALIDATION_KEYS.maxLength),
    satPassword: z
      .string()
      .min(3, VALIDATION_KEYS.minLength)
      .max(14, VALIDATION_KEYS.maxLength),
    confirmSatPassword: z.string(),

    // Review & Confirm
    acceptTerms: z.literal(true, {
      message: VALIDATION_KEYS.required,
    }),
    acceptDataProcessing: z.literal(true, {
      message: VALIDATION_KEYS.required,
    }),
    acknowledgeResponsibility: z.literal(true, {
      message: VALIDATION_KEYS.required,
    }),
  })
  .refine((data) => data.satPassword === data.confirmSatPassword, {
    message: "onboarding.validation.passwordMismatch",
    path: ["confirmSatPassword"],
  });

export type SatOnboardingData = z.infer<typeof satOnboardingSchema>;

export interface OnboardingResult {
  success: boolean;
  message: string;
  connectionId?: string;
  // si el servidor pidió a george-sat verificar la CIEC (entonces se consulta verification-status)
  verificationRequested?: boolean;
  errors?: Record<string, string>;
}

// Get the Onboarding API URL from environment (Lambda SST)
export function getOnboardingApiUrl(): string {
  if (import.meta.env?.VITE_ONBOARDING_API_URL) {
    return import.meta.env.VITE_ONBOARDING_API_URL;
  }
  // Fallback to local development
  return "http://localhost:3001";
}

/**
 * Validates that we're on a secure connection before sending sensitive data.
 *
 * SECURITY: Password is sent in plaintext over HTTPS. TLS provides encryption
 * in transit. The server will encrypt the password using AWS KMS before storing.
 *
 * This is the correct approach because:
 * 1. HTTPS (TLS) provides strong encryption in transit
 * 2. Client-side hashing provides no security benefit (hash becomes the credential)
 * 3. Server needs the original password to authenticate with SAT
 * 4. Server-side KMS encryption provides secure storage
 */
function validateSecureConnection(): void {
  if (typeof window !== "undefined") {
    const isLocalhost = window.location.hostname === "localhost" ||
                        window.location.hostname === "127.0.0.1";
    const isHttps = window.location.protocol === "https:";

    if (!isLocalhost && !isHttps) {
      throw new Error("Secure connection required. Please use HTTPS.");
    }
  }
}

// Error message keys for i18n
export const ERROR_KEYS = {
  VALIDATION_FAILED: "onboarding.error.validationFailed",
  SESSION_NOT_FOUND: "onboarding.error.sessionNotFound",
  FILE_UPLOAD_FAILED: "onboarding.error.fileUploadFailed",
  SUBMISSION_FAILED: "onboarding.error.submissionFailed",
} as const;

// Submission function with server-side validation
export async function submitOnboarding(
  values: FormValues,
  context?: SubmissionContext
): Promise<OnboardingResult> {
  // Validate with Zod client-side first
  const result = satOnboardingSchema.safeParse(values);
  if (!result.success) {
    const errors: Record<string, string> = {};
    result.error.issues.forEach((issue) => {
      const path = issue.path.join(".");
      errors[path] = issue.message;
    });
    return {
      success: false,
      message: ERROR_KEYS.VALIDATION_FAILED,
      errors,
    };
  }

  const validatedData = result.data;

  // Check for session context
  if (!context?.tokenId || !context?.onboardingId) {
    return {
      success: false,
      message: "Session not found. Please refresh and try again.",
    };
  }

  // Validate secure connection before sending sensitive data
  validateSecureConnection();

  const cedulaFile = validatedData.cedulaFiscal;
  const apiUrl = getOnboardingApiUrl();
  const { onboardingId } = context;

  try {
    // Step 1: Get pre-signed URL for S3 upload from Lambda SST
    const uploadUrlResponse = await fetch(`${apiUrl}/api/onboarding/get-upload-url`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        sessionId: onboardingId,
        fileName: cedulaFile.name,
        contentType: cedulaFile.type,
        fileSize: cedulaFile.size,
      }),
    });

    if (!uploadUrlResponse.ok) {
      const errorData = await uploadUrlResponse.json().catch(() => ({}));
      return {
        success: false,
        message: errorData.error || "Failed to prepare file upload. Please try again.",
      };
    }

    const uploadResult = await uploadUrlResponse.json();
    if (!uploadResult.success) {
      return {
        success: false,
        message: uploadResult.error || "Failed to prepare file upload. Please try again.",
      };
    }

    const { uploadUrl, fileKey, requiredHeaders } = uploadResult;

    // Step 2: Upload file directly to S3 using pre-signed URL
    // Must include KMS encryption headers that match the signed URL
    const s3UploadResponse = await fetch(uploadUrl, {
      method: "PUT",
      headers: requiredHeaders || {
        "Content-Type": cedulaFile.type,
      },
      body: cedulaFile,
    });

    if (!s3UploadResponse.ok) {
      return {
        success: false,
        message: "Failed to upload file. Please try again.",
      };
    }

    // Step 3: Submit onboarding via Lambda SST
    // SECURITY: Password sent over HTTPS (encrypted in transit by TLS)
    // Lambda will encrypt with KMS before storing
    const submitPayload = {
      sessionId: onboardingId,
      rfc: validatedData.rfc.toUpperCase(),
      razonSocial: validatedData.razonSocial,
      regimenFiscal: validatedData.regimenFiscal,
      satUsername: validatedData.satUsername,
      satPassword: validatedData.satPassword, // Plaintext over HTTPS, Lambda encrypts with KMS
      cedulaFiscalS3Key: fileKey,
      cedulaFiscalFileName: cedulaFile.name,
      cedulaFiscalSize: cedulaFile.size,
    };

    const response = await fetch(`${apiUrl}/api/onboarding/submit`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(submitPayload),
    });

    const responseData = await response.json();

    if (response.ok && responseData.success) {
      return {
        success: true,
        message: "Onboarding completado exitosamente",
        connectionId: responseData.connectionId,
        verificationRequested: responseData.verificationRequested === true,
      };
    } else {
      return {
        success: false,
        message: responseData.error || "Submission failed. Please try again.",
        errors: responseData.errors,
      };
    }
  } catch (error) {
    // Only log in development to avoid exposing implementation details
    if (import.meta.env.DEV) {
      console.error("[submitOnboarding] Error:", error);
    }
    return {
      success: false,
      message: "Failed to complete onboarding. Please try again.",
    };
  }
}
